import { Link } from "react-router-dom";
import { DAYS } from "@/data/itinerary";
import WeatherWidget from "@/components/WeatherWidget";
import { ArrowUpRight, Clock, MapPin, Utensils, Waves } from "lucide-react";

function formatDate(iso) {
  const d = new Date(iso);
  return d
    .toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })
    .replace(/^./, (c) => c.toUpperCase());
}

export default function Itinerary() {
  return (
    <div data-testid="itinerary-page" className="pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-14">
          <div className="font-accent text-xs text-[#D96C4A] mb-3">
            22 → 29 Agosto 2026
          </div>
          <h1 className="font-display text-5xl md:text-6xl text-[#1D3A4C] leading-tight">
            L'itinerario, giorno per giorno.
          </h1>
          <p className="mt-4 text-[#5C7180] max-w-2xl">
            Otto capitoli scritti sui venti dell'Elba. Ogni giornata è pensata
            per essere flessibile: adatta le spiagge alle condizioni del mare.
          </p>
        </div>

        <div className="space-y-16">
          {DAYS.map((d, i) => (
            <article
              key={d.id}
              data-testid={`itinerary-day-${d.id}`}
              className="grid md:grid-cols-12 gap-8 items-start"
            >
              {/* left: image */}
              <div className="md:col-span-5 relative">
                <Link to={`/giorno/${d.id}`} className="block group">
                  <div className="aspect-[4/5] rounded-2xl overflow-hidden relative">
                    <img
                      src={d.cover}
                      alt={d.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    <div className="absolute top-5 left-5 px-3 py-1 rounded-full bg-white/95 text-xs font-medium text-[#1D3A4C]">
                      Giorno {d.id}
                    </div>
                  </div>
                </Link>
              </div>

              {/* right: content */}
              <div className="md:col-span-7">
                <div className="flex items-center gap-3 text-xs text-[#8A9BA8]">
                  <span className="font-accent">{formatDate(d.date)}</span>
                  <span className="w-1 h-1 rounded-full bg-[#8A9BA8]" />
                  <span className="font-accent">{d.zone}</span>
                </div>
                <h2 className="mt-3 font-display text-3xl md:text-4xl text-[#1D3A4C] leading-tight">
                  {d.title}
                </h2>
                <p className="mt-4 text-[#5C7180] leading-relaxed">
                  {d.story}
                </p>

                <div className="mt-6">
                  <WeatherWidget
                    lat={d.coords.lat}
                    lng={d.coords.lng}
                    date={d.date}
                  />
                </div>

                {/* timeline compact */}
                <div className="mt-8 space-y-2.5">
                  {d.timeline.slice(0, 4).map((t, k) => (
                    <div
                      key={k}
                      className="flex items-baseline gap-4 text-sm"
                    >
                      <span className="font-accent text-[10px] text-[#D96C4A] w-20 shrink-0">
                        {t.time}
                      </span>
                      <span className="text-[#1D3A4C]">{t.text}</span>
                    </div>
                  ))}
                  {d.timeline.length > 4 && (
                    <div className="text-xs text-[#8A9BA8] pl-24">
                      + {d.timeline.length - 4} altre tappe
                    </div>
                  )}
                </div>

                {/* chips */}
                <div className="mt-6 flex flex-wrap gap-2">
                  {d.beaches.slice(0, 3).map((b, k) => (
                    <span
                      key={k}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F5EFE6] text-[#1D3A4C] text-xs"
                    >
                      <Waves className="w-3 h-3" />
                      {b.name}
                    </span>
                  ))}
                  {d.restaurants.slice(0, 2).map((r, k) => (
                    <span
                      key={k}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1D3A4C]/5 text-[#1D3A4C] text-xs"
                    >
                      <Utensils className="w-3 h-3" />
                      {r.name}
                    </span>
                  ))}
                </div>

                <Link
                  to={`/giorno/${d.id}`}
                  data-testid={`itinerary-day-link-${d.id}`}
                  className="mt-8 inline-flex items-center gap-2 text-[#D96C4A] hover:text-[#b8542f] font-medium group"
                >
                  Apri la giornata completa
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
